import { isBefore } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useLocation, useParams } from 'react-router-dom';
import { formatTime } from '../../../core/commom/formatTime';
import { Button } from '../../../core/components/atoms/Button';
import Container from '../../../core/components/atoms/Container';
import { Typography } from '../../../core/components/molecules/Typography';
import Lesson from '../../../core/components/organism/Lesson';
import { MostraSeuAprovaTriggerKey } from '../../../core/components/organism/MostraSeuAprovaModal/data';
import { useMostraSeuAprovaTrigger } from '../../../core/components/organism/MostraSeuAprovaModal/useMostraSeuAprovaTrigger';
import Material from '../../../core/components/organism/Material';
import { markContentAsDone } from '../../../core/queries/course';
import { markLessonFinish } from '../../../core/queries/lessons';
import { markMaterialView } from '../../../core/queries/materials';
import { getViewedFromResponse } from '../../../core/models/contentView';
import { getDisciplineName } from '../../../core/utils/discipline';
import { getVideoConfig } from '../../../core/utils/video';
import WriteEssay from '../../Essay/pages/Writing';
import StartEssay from '../../Essay/pages/start_essay';
import { ContentZero } from '../components/ContentZero';
import { useEssayTheme } from '../hooks/useEssayTheme';
import { MaterialTypes } from '../models/Material.model';
import { useCoursesStore } from '../stores/courseStore';
import { useEssayCourseStore } from '../stores/essayStore';
import { useThemeStore } from '../stores/themeStore';
import { useLessonStore } from '../../Disciplines/stores/lessonStore';
import CourseExercises from './Exercises';
import TextContent from './TextContent';
import NoContent from '../../../core/components/templates/NoContent';

/**
 * Variação da campanha #mostraseuaprova por tipo de curso — a mesma tela exibe
 * todos eles.
 */
const MOSTRA_SEU_APROVA_BY_COURSE_KIND: Record<string, MostraSeuAprovaTriggerKey> =
  {
    regular: 'cursos-regulares',
    regional: 'regionais',
    mini: 'cursos-complementares',
  };

/**
 * O Esquadrão de Elite é um curso como os outros, mas tem variação própria na
 * campanha, então o slug decide antes do tipo. `includes` e não igualdade para
 * acompanhar o resto do módulo, onde o slug pode vir com sufixo de edição.
 */
const isEsquadraoEliteSlug = (slug?: string) =>
  !!slug?.includes('esquadrao-elite');

const Home = React.memo(function Home() {
  const { loadedCourse, isLoadingContent } = useCoursesStore();
  const { isWriting, setIsWriting } = useEssayCourseStore();

  const [essayThemeIsLoading, setEssayThemeIsLoading] = useState(false);

  const { slug } = useParams();
  const { pathname } = useLocation();
  const { theme } = useThemeStore();
  const essayTheme = useEssayTheme();

  const { fire: fireMostraSeuAprova } = useMostraSeuAprovaTrigger();
  // Guarda contra repetição na mesma aula: rever o trecho final faz o player
  // emitir o fim de novo.
  const firedLessonSlug = useRef<string | null>(null);

  // if(!loadedCourse && !isLoadingContent && !essayThemeIsLoading) {
  //   const url = `${pathname}/detalhes`;

  //   return <NoContent url={url} />
  // }

  const contentType = loadedCourse?.selectedContentObject.type as MaterialTypes;

  const essayThemeSlug = loadedCourse?.selectedContentObject?.essayTheme?.slug;

  useEffect(() => {
    setIsWriting(false);

    if (contentType === 'EssayActivity') {
      setEssayThemeIsLoading(true);

      essayTheme.mutate(essayThemeSlug, {
        onSuccess: () => {
          setEssayThemeIsLoading(false);
        },
      });
    }
  }, [contentType, loadedCourse?.selectedContentObject?._contentId, essayThemeSlug]);

  if (!loadedCourse || isLoadingContent || essayThemeIsLoading) {
    return (
      <Container>
        <ContentLoader
          viewBox='0 0 400 160'
          backgroundColor='#f0f0f0'
          foregroundColor='#dedede'
        >
          <rect x='0' y='13' rx='4' ry='4' width='400' height='9' />
          <rect x='0' y='29' rx='4' ry='4' width='100' height='8' />
          <rect x='0' y='50' rx='4' ry='4' width='400' height='10' />
          <rect x='0' y='65' rx='4' ry='4' width='400' height='10' />
          <rect x='0' y='79' rx='4' ry='4' width='100' height='10' />
          <rect x='0' y='99' rx='5' ry='5' width='400' height='200' />
        </ContentLoader>
      </Container>
    );
  }

  const startEssay = () => {
    setIsWriting(true);
  };

  async function setContentAsDone() {
    const contentParams = {
      courseId: loadedCourse.id,
      programId: loadedCourse.selectedProgram,
      contentId: loadedCourse.selectedContentObject._contentId,
    };

    await markContentAsDone(
      contentParams.courseId,
      contentParams.programId,
      contentParams.contentId
    );

    dispatchSetContentAsDoneEvent();
  }

  function dispatchSetContentAsDoneEvent() {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: loadedCourse.selectedContentObject._contentId,
      },
    });
    window.dispatchEvent(setContentAsDone);
  }

  if (isBefore(new Date(), new Date(loadedCourse.startDate))) {
    return <ContentZero contentZero={loadedCourse.contentZero} />;
  }

  if (contentType === 'Text') {
    return <TextContent content={loadedCourse.selectedContentObject} />;
  }

  if (contentType === 'Lesson') {
    const selectedContentObject = loadedCourse.selectedContentObject;
    const lesson = selectedContentObject.target;

    if (!lesson || !selectedContentObject) {
      const url = `${pathname}/detalhes`;

      return <NoContent url={url} />;
    }

    const video = {
      defaultPlayer: lesson.defaultPlayer,
      sambatech_token: lesson.sambatech_token,
      vimeoID: lesson.vimeoID,
    };

    const { playerType, token } = getVideoConfig(video);

    // Quando o default é o player novo (aprova), o <Player> clássico (usado como
    // fallback quando a aula não traz embedUrl) não entende esse tipo, então
    // derivamos um player legado a partir dos tokens disponíveis. Mesma lógica
    // de Disciplines/pages/Lesson. A decisão final entre novo e legado fica no
    // <Lesson>/LessonPlayer, que prioriza o embedUrl.
    const legacyPlayerType =
      lesson.defaultPlayer === 'aprova'
        ? lesson.vimeoID
          ? 'vimeo'
          : 'sambatech'
        : playerType;
    const legacyToken =
      lesson.defaultPlayer === 'aprova'
        ? legacyPlayerType === 'vimeo'
          ? lesson.vimeoID
          : lesson.sambatech_token
        : token;

    const teacher = (lesson.teacher || [])
      .map((it: { name: any }) => it.name)
      .join(', ');

    const onFinish = async () => {
      setContentAsDone();
      // Automático (assistiu a aula): sempre marca como vista (viewed: true),
      // para não desmarcar ao reassistir uma aula já concluída.
      const result = await markLessonFinish(lesson?.slug, {
        course: loadedCourse.id,
        program: loadedCourse.selectedProgram,
        origin: 'courses',
        viewed: true,
      });

      // Toggle: reflete o status retornado ao sincronizar o useLessonStore.
      const viewed = getViewedFromResponse(result);
      const lessonStore = useLessonStore.getState();
      const updatedLessons = lessonStore.lessons.map((l) =>
        l.slug === lesson?.slug ? { ...l, done: viewed } : l
      );
      lessonStore.setLessons(updatedLessons);

      // Campanha #mostraseuaprova, só quando o vídeo termina de verdade. A
      // conclusão virtual chega por volta dos 80% e marca a aula como assistida,
      // mas o aluno ainda está assistindo — o modal cobriria o player.
      // `fire` é no-op fora da campanha ou com o aluno inelegível, e nunca lança.
      if (origin !== 'ended') return;

      const campaignTrigger = isEsquadraoEliteSlug(slug)
        ? 'esquadrao-de-elite'
        : MOSTRA_SEU_APROVA_BY_COURSE_KIND[loadedCourse.kind];

      if (campaignTrigger && firedLessonSlug.current !== lesson?.slug) {
        firedLessonSlug.current = lesson?.slug ?? null;
        fireMostraSeuAprova(campaignTrigger);
      }
    };

    return (
      <Container>
        <Lesson
          id={lesson.id}
          title={lesson.title}
          discipline={getDisciplineName(lesson?.ancestors?.area?.slug)}
          time={formatTime(lesson.duration)}
          teacher={teacher}
          pdfSummary={lesson.pdfSummaryUri}
          notesPdfUri={lesson.notesPdfUri}
          timeline={lesson.timeline}
          timelineText='Tópicos da vídeoaula'
          playerType={legacyPlayerType}
          token={legacyToken}
          embedUrl={lesson.embedUrl}
          defaultPlayer={lesson.defaultPlayer}
          showFavorite={false}
          isFavorite={lesson.favorite}
          review={selectedContentObject.review}
          contentSlug={selectedContentObject?.target?.slug}
          viewOrigin='courses'
          onFinish={onFinish}
          courseId={loadedCourse.id}
          showQuiz={true}
        />
      </Container>
    );
  }

  if (contentType === 'Workbook' || contentType === 'Mindmap') {
    const materialTypeName =
      contentType === 'Workbook' ? 'Apostila' : 'Mapa mental';
    const selectedContentObject = loadedCourse.selectedContentObject;
    const material = selectedContentObject.target;

    if (!material || !selectedContentObject) {
      const url = `${pathname}/detalhes`;

      return <NoContent url={url} />;
    }

    const handleMarkMaterialView = () => {
      // "opened" é toggle: só marca quando ainda não está visto, para reabrir
      // um material já concluído não desmarcá-lo.
      if (selectedContentObject.done) return;

      setContentAsDone();
      const materialViewType =
        contentType === 'Mindmap' ? 'mindmap' : 'workbook';
      markMaterialView(material?.slug, materialViewType, 'courses');
    };

    return (
      <Container>
        <Material
          materialId={material.id}
          materialSlug={material?.slug}
          file={material.uri}
          discipline={getDisciplineName(material?.ancestors?.area?.slug)}
          materialType={materialTypeName}
          title={material.title}
          showFavorite={false}
          review={selectedContentObject.review}
          markMaterialView={handleMarkMaterialView}
        />
      </Container>
    );
  }

  if (contentType === 'EssayActivity') {
    if (isWriting) {
      return <WriteEssay key={theme?.slug} origin='course' slug={theme?.slug} />;
    }

    return (
      <StartEssay
        key={theme?.slug}
        isCourse={true}
        slug={theme?.slug}
        course={slug}
        onClickWrite={startEssay}
      />
    );
  }

  if (contentType === 'Exercise') {
    return <CourseExercises setContentAsDone={setContentAsDone} />;
  }

  if (contentType === 'Link') {
    return (
      <Container className='flex flex-col gap-md text-center'>
        <Typography type='titleSection' size='h4'>
          Link Externo
        </Typography>
        <Typography type='title' size='h2'>
          {loadedCourse?.selectedContentObject.title}
        </Typography>
        <Button
          LeftIcon={'link'}
          variant='text'
          asLink
          to={loadedCourse?.selectedContentObject.value}
          target='_blank'
          full
          onClick={() => {
            if (!loadedCourse?.selectedContentObject.done) {
              setContentAsDone();
            }
          }}
        >
          Abrir link
        </Button>
      </Container>
    );
  }

  return <div />;
});

export default Home;
